import React from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { FileResponseInfoFragment, GeneratedReportMetaInfoFragment } from 'src/api';
import { JsonRenderer, PdfRenderer } from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { SxProp } from 'src/types/mui';

import {
  GeneratedReportPreviewCard,
  GeneratedReportPreviewCardAction,
} from '../GeneratedReportPreviewCard';

export type GeneratedReportsPdfRendererProps = {
  generatedReportsMeta: GeneratedReportMetaInfoFragment[];
  pdfPreview: FileResponseInfoFragment | null;
  onPreview: (report: GeneratedReportMetaInfoFragment) => Promise<void>;
  onResetPreview: () => void;
  onDownload: (reportFileId: string) => Promise<void>;
  cardActions?: GeneratedReportPreviewCardAction[];
  isSingleReport?: boolean;
};

export const GeneratedReportsPdfRenderer: React.FC<GeneratedReportsPdfRendererProps> = ({
  generatedReportsMeta,
  pdfPreview,
  onPreview,
  onResetPreview,
  onDownload,
  cardActions,
  isSingleReport,
}) => {
  const { isMobile } = useScreenInfo();

  const [downloading, setDownloading] = React.useState(false);

  const handleReportDownload = React.useCallback(async () => {
    if (!pdfPreview) return;
    setDownloading(true);

    try {
      await onDownload(pdfPreview.id);
    } finally {
      setDownloading(false);
    }
  }, [onDownload, pdfPreview]);

  return (
    <Box height={isMobile ? 'auto' : '380px'} width="100%" overflow={isMobile ? undefined : 'auto'}>
      {pdfPreview ? (
        <Card variant="outlined" sx={pdfPreviewCardContainerStyles}>
          <CardHeader
            title={<Typography>{pdfPreview.filename}</Typography>}
            action={
              <Stack direction="row" gap={2}>
                {!isSingleReport && (
                  <Button variant="outlined" onClick={onResetPreview} disabled={downloading}>
                    Back to files
                  </Button>
                )}
                <IconButton
                  color="primary"
                  onClick={handleReportDownload}
                  disabled={downloading}
                  loading={downloading}
                >
                  <DownloadIcon />
                </IconButton>
              </Stack>
            }
          />
          <Divider />
          <CardContent sx={pdfPreviewCardContentStyles}>
            {pdfPreview.mime === 'application/json' ? (
              <JsonRenderer
                src={pdfPreview.downloadUrl || undefined}
                containerStyles={pdfRendererContainerStyles}
              />
            ) : (
              <PdfRenderer
                src={pdfPreview.downloadUrl || undefined}
                containerStyles={pdfRendererContainerStyles}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2} height="inherit">
          {generatedReportsMeta.map(generatedReport => (
            <Grid
              key={generatedReport.id}
              size={{
                xs: 12,
                sm: isSingleReport ? 12 : 6,
                md: isSingleReport ? 12 : 3,
              }}
            >
              <GeneratedReportPreviewCard
                generatedReportMeta={generatedReport}
                actions={cardActions}
                onPreview={onPreview}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

const pdfPreviewCardContainerStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const pdfPreviewCardContentStyles: SxProp = {
  padding: 0,
  paddingBottom: '0 !important', // overwrite default mui styles
  overflow: 'hidden',
  height: '100%',
};

const pdfRendererContainerStyles: SxProp = { border: 'none', height: 'inherit', overflow: 'auto' };
